'use client'

import { Badge } from '@tailark/core/ui/badge'
import { Button } from '@tailark/core/ui/button'
import { X } from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import { useEffect } from 'react'
import { categoryLabelFa, templatePreviewCopyFa } from '../templates-copy-fa'
import type { Template } from '../types'

interface TemplatePreviewModalProps {
    template: Template | null
    isOpen: boolean
    onClose: () => void
}

export function TemplatePreviewModal({ template, isOpen, onClose }: TemplatePreviewModalProps) {
    useEffect(() => {
        if (!isOpen) return

        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.addEventListener('keydown', handleEscKey)
        return () => document.removeEventListener('keydown', handleEscKey)
    }, [isOpen, onClose])

    if (!isOpen || !template) return null

    const handleUseTemplate = () => {
        window.open(`https://app.lummeny.com/template?id=${template.templateId}`, '_blank')
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-background relative mx-4 w-full max-w-2xl overflow-hidden rounded-lg shadow-xl"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b p-4">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">{template.icon}</span>
                        <div>
                            <h2 className="text-xl font-semibold">{template.title}</h2>
                            <Badge
                                variant="secondary"
                                className="rounded-full">
                                {categoryLabelFa(template.category)}
                            </Badge>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        aria-label={templatePreviewCopyFa.closeAriaLabel}
                        className="h-8 w-8">
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                <div className="p-6">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="border-border mb-6 rounded-lg border p-4">
                            <Image
                                src={template.image}
                                alt={template.title}
                                width={300}
                                height={600}
                                className="mb-4 h-48 w-full rounded-lg object-contain"
                            />
                        </div>

                        <div className="mb-6">
                            <h3 className="mb-3 text-lg font-semibold">{templatePreviewCopyFa.keyFeatures}</h3>
                            <div className="gap-2">
                                {template.features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-2 text-sm">
                                        <div className="bg-primary h-2 w-2 rounded-full"></div>
                                        {feature}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <p className="text-muted-foreground mb-4 leading-relaxed">{template.description}</p>

                    <div className="flex gap-3">
                        <Button
                            onClick={handleUseTemplate}
                            className="max-w-xs flex-1"
                            size="lg">
                            {templatePreviewCopyFa.useTemplate}
                        </Button>
                    </div>
                </div>
            </motion.div>
        </div>
    )
}
